import React from 'react';
import axios from 'axios';
import { useInfiniteQuery, useQuery } from 'react-query';
import jsonpAdapter from '../common/axios-jsonp';
import { getSolrVal, langcode } from '../common/catalogUtils';
import { HtmlCustom } from '../common/HtmlCustom';
import { useBiblStore } from './useBiblStore';
import { useCatSearchStore } from './useCatSearchStore';

/**
 * usePage: a hook implementing React Query's useInfiniteQuery
 *
 * @param pgid
 *    the solr page ID
 * @param byPass
 *    whether or not this query can be bypassed
 * @returns {UseInfiniteQueryResult<boolean|{next: boolean, page: JSX.Element}, unknown>}
 */
export function useInfinitePage(pgid, byPass = false) {
    const edbibl = useBiblStore((state) => state.edBibl);
    let qwrds = useCatSearchStore((state) => state.getQueryWords());
    if (typeof qwrds == 'string') {
        qwrds = [qwrds];
    }
    // console.log("edbibl", edbibl, pgid);
    return useInfiniteQuery(
        ['digpage', pgid],
        ({ pageParam = pgid, eb = edbibl }) => pagequery(pageParam, eb, qwrds),
        {
            getPreviousPageParam: (lastPage, allPages) => lastPage.prev,
            getNextPageParam: (lastPage, allPages) => lastPage.next,
        }
    );
}

const pagequery = async (pgid, edbibl, qwrds) => {
    /**
     * pagequery is the SOLR query function to retrieve page text records (type:textpg)
     *
     * Returns an object with:
     *  page: the text of the page
     *  next: the id of the next page or false
     *  prev: the id of the previous page or false
     */

    // console.log("in pagequery:", pgid);
    if (!pgid || pgid === '') {
        return false;
    }
    const coll = edbibl?.coll;
    const ed = edbibl?.edsig;
    // console.log("pgid", pgid, coll, ed);
    const altpgid = pgid.includes('v0')
        ? pgid.replace('v0', 'v')
        : pgid.replace('v', 'v0');

    let request = {
        url: process.env.REACT_APP_SOLR_URL,
        adapter: jsonpAdapter,
        callbackParamName: 'json.wrf',
        params: {
            q: `id:*${pgid} OR id:*${altpgid}`,
            fq: ['type:textpg', `coll:${coll}`, `edsig:${ed}`],
            wt: 'json',
        },
    };

    const reply = await axios.request(request).catch(function (e) {
        console.log('error', e);
    });
    let data = reply?.data;
    let pgtext = false,
        nxtpg = '',
        prevpg = '';
    if (data && data?.response && data.response?.numFound > 0) {
        //console.log("data", data);
        const sdoc = data.response.docs[0];
        const langval = sdoc?.lang_s || sdoc?.lang; // switched to lang_s but keeping this in case there are old .lang fields
        const pglang = langcode(getSolrVal(langval));
        pgtext = sdoc[`pg_mu_${pglang}`];
        if (!pgtext || pgtext?.length === 0) {
            pgtext = sdoc[`pg_${pglang}`];
        }
        if (sdoc?.nextpage_s || sdoc?.prevpage_s) {
            if (sdoc?.vnum_i) {
                nxtpg = `v${sdoc.vnum_i}p${sdoc.nextpage_s}`;
                prevpg = `v${sdoc.vnum_i}p${sdoc.prevpage_s}`;
            } else {
                // For digital pages where there are no volumes. Pagination for each text begins at 1.
                nxtpg = `p${sdoc.nextpage_s}`;
                prevpg = `p${sdoc.prevpage_s}`;
            }
            if (sdoc?.prevpage_s?.length === 0) {
                prevpg = '';
            }
            if (sdoc?.nextpage_s?.length === 0) {
                nxtpg = '';
            }
        }
    }
    if (Array.isArray(pgtext)) {
        pgtext = pgtext[0];
    }

    // Check if Search is active and there is query words, if so, highlight them on page
    let hlpg = pgtext;
    // console.log("in page function", qwrds, hlpg);
    if (typeof hlpg === 'string' && hlpg.length > 0 && qwrds?.length > 0) {
        hlpg = qwrds.reduce((pgtxt, wrd) => {
            return pgtxt.split(wrd).join(`<span class="hl">${wrd}</span>`);
        }, hlpg);
    }
    pgtext = <HtmlCustom markup={hlpg} key={pgid} />;
    const retdata = {
        page: pgtext,
        next: nxtpg,
        prev: prevpg,
    };
    return retdata;
};
